import DataRequest from "../../../services/dataRequest";
import { getCookieValue, getGlobalVal } from "../../../utilities/utils";
import { formatEstimate, storesData } from "../ez-quote.helpers";
import { Item, StoreAddress, StoreOption } from "../ez-quote.types";
import { isDesignerReviewBrand } from "../../../constants/brandFeatures";

type QuoteHandlerDeps = {
  items: Item[];
  brand: string | null;
  sourceURL: string | null;
  quoteId: string | null;
  selectedStoreOption: StoreOption | null;
  nearbyStores: StoreOption[];
  setLoading: (v: boolean) => void;
  setItems: (v: any) => void;
  setTotalEstimate: (v: { min: number; max: number }) => void;
  setError: (v: string | null) => void;
  setquoteId: (v: string | null) => void;
  setCurrentStep: (v: number) => void;
  setIsOtpModalVisible: (v: boolean) => void;
  setOtpModalMode: (v: "phone" | "email") => void;
  setStoreAddress: (v: StoreAddress | null) => void;
  setZipCodeFromModal: (v: string | null) => void;
  setSelectedStoreOption: (v: StoreOption | null) => void;
  setNearbyStores: (v: StoreOption[]) => void;
  setEmailSendCount: (v: any) => void;
  setSmsDeliveryStatus: (v: string | null) => void;
  showToast: (msg: string, retry?: () => void, variant?: "error" | "primary") => void;
};

export function useQuoteHandlers({
  items, brand, sourceURL, quoteId, selectedStoreOption, nearbyStores,
  setLoading, setItems, setTotalEstimate, setError, setquoteId, setCurrentStep,
  setIsOtpModalVisible, setOtpModalMode, setStoreAddress,
  setZipCodeFromModal, setSelectedStoreOption, setNearbyStores, setEmailSendCount,
  setSmsDeliveryStatus, showToast,
}: QuoteHandlerDeps) {
  const generateRequestId = () =>
    `IQ_req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const getQuote = async (
    phoneNumber: string,
    zipCode: string,
    selectedStore: StoreOption | null = null,
    nearbyStoresList: StoreOption[] = [],
    smsConsent: boolean = false,
    callConsent: boolean = false,
    customerName?: string
  ) => {
    const retry = () =>
      getQuote(phoneNumber, zipCode, selectedStore, nearbyStoresList, smsConsent, callConsent, customerName);
    try {
      setLoading(true);
      window.scrollTo({ top: 0, behavior: "smooth" });

      setTimeout(() => {
        try {
          (window as any)?.exponea?.showWebLayer(getGlobalVal("WEBLAYER_SURVEY_ACCESS_TOKEN"));
          (window as any)?.exponea?.identify(
            { phone_id: phoneNumber },
            { phone: phoneNumber },
            function () {},
            function () {},
            false
          );
        } catch (error) {
          console.error(error);
        }
      }, 5000);

      if (!selectedStore || !nearbyStoresList.length) {
        setLoading(false);
        setIsOtpModalVisible(true);
        return;
      }

      const storeNumber = selectedStore.storeNumber;
      const payload = {
        requestId: generateRequestId(),
        store: { storeEzid: storeNumber },
        // LW-1477: extensible node consumed by ezQuoteService to attribute
        // leads to their originating (paid vs. organic) website.
        ...(sourceURL ? { additionalInfo: { sourceURL } } : {}),
        customer: {
          ...(customerName ? { customerName } : {}),
          phone: phoneNumber,
          cookie: getCookieValue("__exponea_etc__"),
          smsConsent,
          callConsent,
          brand: brand || storesData?.data?.[0]?.brandName || "ezpawn",
          nearbyStores: nearbyStoresList.map((store) => store.storeNumber),
        },
        items: items.map((item) => {
          const isJewelryItem = item.condition === null && item.textAreaAttributeKeys !== undefined;
          const metalType = item.goldTypeName ?? "Gold";
          const isGoldMetal = metalType === "Gold";
          const diamondsEntry = Object.entries(item.attributes).find(([key]) =>
            key.toLowerCase().includes("diamond")
          );
          const hasDiamond =
            isJewelryItem && diamondsEntry
              ? diamondsEntry[1].id?.toLowerCase() === "yes"
                ? "true"
                : "false"
              : undefined;
          const brandedEntry = Object.entries(item.attributes).find(([key]) =>
            key.toLowerCase().includes("branded")
          );
          const isNotDesigner =
            isDesignerReviewBrand(brand) && isJewelryItem && brandedEntry
              ? brandedEntry[1].id?.toLowerCase() === "yes"
                ? null
                : "true"
              : undefined;
          return {
            categoryID: item.apiCategoryId ?? item.category.category_id,
            categoryName: item.apiCategoryName ?? item.category.category_name,
            ...(isJewelryItem && { rootCategoryID: "500002" }),
            itemCondition: item.condition?.id,
            attribute: [
              ...Object.entries(item.attributes)
                .filter(([key]) => !key.toLowerCase().includes("diamond") && !key.toLowerCase().includes("branded"))
                .map(([key, value]) => {
                  const isKarat = isGoldMetal && key.toLowerCase().includes("karat");
                  const rawValue =
                    value.name === "Other/I'm not sure" || value.id == "other"
                      ? null
                      : key == "Model Number"
                      ? value.id
                      : value.name;
                  const finalValue =
                    isKarat && rawValue ? rawValue.replace(/\s*\(.*?\)/g, "").trim() : rawValue;
                  return { name: isKarat ? "Gold Karat" : key, value: finalValue };
                })
                .filter((attr) => attr.value !== ""),
              ...(item.rootCategoryName
                ? [{ name: "rootCategoryName", value: item.rootCategoryName }]
                : []),
              ...(isJewelryItem
                ? [
                    { name: "Metal Type", value: metalType },
                    ...(isGoldMetal ? [{ name: "Gold Color", value: "Y" }] : []),
                    { name: "Scrap or not scrap", value: "Retail" },
                    ...(hasDiamond !== undefined
                      ? [{ name: "hasDiamond", value: hasDiamond }]
                      : []),
                    ...(isNotDesigner !== undefined
                      ? [{ name: "isNotDesigner", value: isNotDesigner }]
                      : []),
                  ]
                : []),
            ],
          };
        }),
      };

      const response = await DataRequest.execute(
        "quotation/sms",
        { method: "POST", postData: JSON.stringify(payload) },
        { "Content-Type": "application/json" },
        true
      );

      if (response?.data?.success) {
        const quotePayload = response.data.payload;
        setStoreAddress(quotePayload.store.address);
        const updatedItems = items.map((item, index) => {
          const quoteItem = quotePayload.itemList[index];
          return {
            ...item,
            estimate:
              quoteItem.minQuote != null && quoteItem.maxQuote != null
                ? `$${formatEstimate(quoteItem.minQuote)} - $${formatEstimate(quoteItem.maxQuote)}`
                : "N/A",
          };
        });
        setItems(updatedItems);
        setTotalEstimate({ min: quotePayload.minQuote, max: quotePayload.maxQuote });
        setError(null);
        setquoteId(quotePayload?.ezQuoteId);
        setSmsDeliveryStatus(quotePayload.smsResponse?.success === false ? "WIW_SMS_DELIVERY_FAILURE" : null);
        setCurrentStep(6);
      } else {
        const errorMessage =
          response.data?.error?.message || "Quote not available at the moment.";
        showToast(errorMessage, retry);
        setError(errorMessage);
      }
    } catch (error) {
      console.error("Error:", error);
      showToast("Something went wrong. Please try again.", retry);
      setError("An error occurred while fetching the quote.");
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToReview = async () => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (Number(getGlobalVal("VERIFY_EMAIL_OTP")) === 0) {
      setOtpModalMode("phone");
      setIsOtpModalVisible(true);
      return;
    }

    setLoading(true);
    try {
      const response = await DataRequest.execute(
        "session",
        { method: "GET" },
        { "Content-Type": "application/json" }
      );
      const session = response.data?.payload?.[0];
      if (session && session.email?.length) {
        setLoading(false);
        getQuote(
          session.email,
          session.zipCode,
          selectedStoreOption,
          nearbyStores
        );
        return;
      }
    } catch (err) {
      console.error("Error checking session:", err);
    } finally {
      setLoading(false);
    }

    setOtpModalMode("phone");
    setIsOtpModalVisible(true);
  };

  const handleOtpVerified = (
    phoneNumber: string,
    zipCode: string,
    selectedStore: StoreOption | null,
    nearbyStoresList: StoreOption[],
    smsConsent: boolean,
    callConsent: boolean,
    customerName?: string
  ) => {
    setIsOtpModalVisible(false);
    setZipCodeFromModal(zipCode);
    setSelectedStoreOption(selectedStore);
    setNearbyStores(nearbyStoresList);
    getQuote(phoneNumber, zipCode, selectedStore, nearbyStoresList, smsConsent, callConsent, customerName);
  };

  const handleEmailSent = () => {
    if (quoteId) {
      setEmailSendCount((prev: { [key: string]: number }) => ({
        ...prev,
        [quoteId!]: (prev[quoteId!] || 0) + 1,
      }));
    }
  };

  return { getQuote, handleProceedToReview, handleOtpVerified, handleEmailSent };
}
